import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import type { CertPrimaryJobChangePreviewRow } from "../../../../../utils/models";

interface JobMappingChangeConfirmDialogProps {
	open: boolean;
	roleString: string;
	/** Display label of the primary job the mapping currently syncs as (the "from" side of the title). */
	currentJobLabel: string;
	/** Display label of the new primary job the mapping is changing to (the "to" side, and each will-change row's target). */
	newJobLabel: string;
	/** Active employees linked to the role string, each flagged whether their first job will be overwritten on save. */
	rows: CertPrimaryJobChangePreviewRow[];
	onConfirm: () => void;
	onClose: () => void;
}

/**
 * Confirmation shown before saving a primary-job change on an existing cert role-string mapping (DAT-535).
 * Purely informational: it lists every active linked employee split into those whose first job WILL change
 * (current job → new job) and those who WON'T (with their current job), so the propagation the backend
 * applies on save is reviewed first. Confirm applies the change; Cancel reverts the edit (no opt-out).
 */
export const JobMappingChangeConfirmDialog: FC<JobMappingChangeConfirmDialogProps> = ({
	open,
	roleString,
	currentJobLabel,
	newJobLabel,
	rows,
	onConfirm,
	onClose,
}) => {
	const { t } = useTranslation();

	const { willChange, wontChange } = useMemo(
		() => ({
			willChange: rows.filter((r) => r.willChange),
			wontChange: rows.filter((r) => !r.willChange),
		}),
		[rows],
	);

	const jobLabel = (row: CertPrimaryJobChangePreviewRow) => row.currentJob?.displayName ?? row.currentJob?.name ?? t("hris.mapping.jobChangeConfirm.noJob");

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("hris.mapping.jobChangeConfirm.title", { roleString, currentJob: currentJobLabel, newJob: newJobLabel })}
			dataTestId="hris-mapping-job-change-confirm-dialog"
			confirm={t("hris.mapping.jobChangeConfirm.confirm")}
			onConfirm={onConfirm}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1, minWidth: 420, maxHeight: 420, overflowY: "auto" }}>
					<Typography variant="body2" color="text.secondary">
						{t("hris.mapping.jobChangeConfirm.description")}
					</Typography>
					{willChange.length > 0 && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }} data-testid="hris-mapping-job-change-will-section">
							<Typography variant="subtitle2" color="text.primary">
								{t("hris.mapping.jobChangeConfirm.willChangeHeader", { count: willChange.length })}
							</Typography>
							{willChange.map((row) => (
								<Typography key={row.nurseId} variant="body2" color="text.secondary" data-testid={`hris-mapping-job-change-will-${row.nurseId}`}>
									{t("hris.mapping.jobChangeConfirm.willChangeRow", { name: row.name, currentJob: jobLabel(row), newJob: newJobLabel })}
								</Typography>
							))}
						</Box>
					)}
					{wontChange.length > 0 && (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }} data-testid="hris-mapping-job-change-wont-section">
							<Typography variant="subtitle2" color="text.primary">
								{t("hris.mapping.jobChangeConfirm.wontChangeHeader", { count: wontChange.length })}
							</Typography>
							{wontChange.map((row) => (
								<Typography key={row.nurseId} variant="body2" color="text.secondary" data-testid={`hris-mapping-job-change-wont-${row.nurseId}`}>
									{t("hris.mapping.jobChangeConfirm.wontChangeRow", { name: row.name, currentJob: jobLabel(row) })}
								</Typography>
							))}
						</Box>
					)}
				</Box>
			}
		/>
	);
};
